import authService from './auth.service';
import type {
  PresenceStatus,
  PresenceStatusesListResponse,
  PresenceStatusSortField,
  SortDirection,
  PresenceStatusTemplate,
  PresenceStatusUpdatePayload,
} from '../types/presence-status.types';

const API_URL = '/api/v1';

class PresenceStatusesService {
  async listPresenceStatuses(params?: {
    limit?: number;
    offset?: number;
    sort?: PresenceStatusSortField;
    direction?: SortDirection;
    is_system?: boolean;
  }): Promise<PresenceStatusesListResponse> {
    const { limit = 20, offset = 0, sort = 'name', direction = 'asc', is_system } = params || {};
    const url = new URL(`${API_URL}/presence-statuses`, window.location.origin);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('offset', String(offset));
    if (sort) url.searchParams.set('sort', sort);
    if (direction) url.searchParams.set('direction', direction);
    if (typeof is_system === 'boolean') url.searchParams.set('is_system', String(is_system));

    const res = await authService.fetchWithAuth(url.pathname + url.search);
    const data = await res.json().catch(() => null);

    if (!res.ok) {
      const message = (data as any)?.detail || 'Не удалось загрузить статусы присутствия';
      throw new Error(message);
    }

    const items: PresenceStatus[] = Array.isArray((data as any)?.items)
      ? (data as any).items
      : Array.isArray(data)
        ? (data as PresenceStatus[])
        : [];
    const totalRaw = (data as any)?.total;
    const total = typeof totalRaw === 'number' ? totalRaw : items.length;

    return { items, total };
  }

  async listPresenceStatusTemplates(): Promise<PresenceStatusTemplate[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/presence-status-templates`);
    const data = await res.json().catch(() => null);

    if (!res.ok) {
      const message = (data as any)?.detail || 'Не удалось загрузить шаблоны статусов';
      throw new Error(message);
    }

    return Array.isArray(data) ? (data as PresenceStatusTemplate[]) : [];
  }

  async createPresenceStatus(payload: {
    name: string;
    color: string;
    description: string;
    is_active: boolean;
    manual_selectable: boolean;
    template_id: number;
  }): Promise<PresenceStatus> {
    const res = await authService.fetchWithAuth(`${API_URL}/presence-statuses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => null);

    if (!res.ok) {
      const message = (data as any)?.detail || 'Не удалось создать статус пользователя';
      throw new Error(message);
    }

    return data as PresenceStatus;
  }

  async getPresenceStatus(id: number): Promise<PresenceStatus> {
    const res = await authService.fetchWithAuth(`${API_URL}/presence-statuses/${id}`);
    const data = await res.json().catch(() => null);

    if (!res.ok) {
      const message = (data as any)?.detail || 'Не удалось загрузить статус пользователя';
      throw new Error(message);
    }

    return data as PresenceStatus;
  }

  async updatePresenceStatus(id: number, payload: PresenceStatusUpdatePayload): Promise<PresenceStatus> {
    const res = await authService.fetchWithAuth(`${API_URL}/presence-statuses/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => null);

    if (!res.ok) {
      const message = (data as any)?.detail || 'Не удалось обновить статус';
      throw new Error(message);
    }

    return data as PresenceStatus;
  }

  async activatePresenceStatus(id: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/presence-statuses/${id}:activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const data = await res.json().catch(() => null);
      const message = (data as any)?.detail || 'Не удалось сменить статус пользователя';
      throw new Error(message);
    }
  }

  async deletePresenceStatus(id: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/presence-statuses/${id}`, {
      method: 'DELETE',
    });

    if (!res.ok) {
      const data = await res.json().catch(() => null);
      const message = (data as any)?.detail || 'Не удалось удалить статус';
      throw new Error(message);
    }
  }
}

export default new PresenceStatusesService();
